import { useCallback, useEffect, useState } from 'react';
import { Button } from './button';
import { Textarea } from './textarea';
import { GoogleSearchPreview, type GoogleSearchSettings } from './recruitment-google';

export interface ApprovalApi {
  get<T>(path: string, config?: { responseType: 'blob' }): Promise<{ data: T }>;
  post<T>(path: string, body: unknown): Promise<{ data: T }>;
}
interface QueueItem {
  id: string;
  name: string;
  version: number;
  state: string;
}
interface Asset {
  assetId: string;
  sha256: string;
  mediaType: string;
  objectKey: string;
}
interface Revision {
  campaignId: string;
  version: number;
  currentVersion: number;
  contentHash: string;
  state: string;
  snapshot: {
    name: string;
    site_id: string;
    study_ids: string[];
    disease_focus: string;
    commissioningOrganizationId: string;
    payerOrganizationId: string | null;
    placements: {
      googleSearch?: GoogleSearchSettings | null;
      channel: string;
      placement: string;
      wording: string;
      assets: Asset[];
      sourceId: string;
      entryUrl: string;
      conversationVariant: string;
    }[];
    targeting: { locations: string[]; minimumAge: number; keywords: string[] };
    startsOn: string;
    endsOn: string;
    publicIdentity: string;
    allocationsCents: Record<string, number>;
    totalBudgetCents: number;
    managementFeeCents: number;
    testPlan: {
      limitCents: number;
      durationDays: number;
      comparison: string;
      pauseAtLimit: boolean;
    };
  };
  evidence: { id: string; name: string; sha256: string; uploadedBy: string }[];
  history: {
    version: number;
    actorId: string;
    decision: string;
    at: string;
    reason?: string;
    exemptionExplanation?: string;
    evidenceIds?: string[];
  }[];
}
const states: Record<string, string> = {
  prepared: 'Zur Annahme bereit',
  accepted: 'Vom Kunden akzeptiert',
  approved: 'Materialien freigegeben',
  returned: 'Zur Änderung zurückgegeben',
  rejected: 'Abgelehnt',
  superseded: 'Durch neuere Revision ersetzt',
};
const money = (value: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value / 100);
function failure(error: unknown): string {
  const status = (error as { response?: { status?: number } }).response?.status;
  if (status === 409)
    return 'Die Revision oder der Prüfstatus wurde geändert. Bitte erneut laden und die aktuellen Materialien prüfen.';
  if (status === 403 || status === 404)
    return 'Keine Berechtigung oder Revision nicht mehr verfügbar.';
  const message = (error as { response?: { data?: { error?: string } } }).response?.data?.error;
  return (
    message ??
    'Die Aktion ist fehlgeschlagen. Ihre Eingaben bleiben erhalten. Bitte erneut versuchen.'
  );
}
function VersionImage({ api, base, asset }: { api: ApprovalApi; base: string; asset: Asset }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    api
      .get<Blob>(`${base}/assets/${asset.assetId}`, { responseType: 'blob' })
      .then(({ data }) => {
        if (active) {
          objectUrl = URL.createObjectURL(data);
          setUrl(objectUrl);
        }
      })
      .catch(error => {
        if (active) setError(failure(error));
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, base, asset.assetId]);
  return (
    <figure className="space-y-2">
      {error ? (
        <p role="alert">{error}</p>
      ) : url ? (
        <img
          src={url}
          alt="Ausgewählte Anzeigenbildversion"
          className="max-h-80 rounded-3xl object-contain"
        />
      ) : (
        <p>Bildversion wird geladen…</p>
      )}
      <figcaption className="break-all text-xs">
        Bildversion {asset.assetId}, SHA-256 {asset.sha256}
      </figcaption>
    </figure>
  );
}

export function RecruitmentApproval({
  api,
  operator = false,
}: {
  api: ApprovalApi;
  operator?: boolean;
}) {
  const base = `${operator ? '/cortex' : ''}/clinical-trials/marketing/approval`;
  const [items, setItems] = useState<QueueItem[]>([]);
  const [revision, setRevision] = useState<Revision | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [exemption, setExemption] = useState('');
  const [evidenceIds, setEvidenceIds] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const reload = useCallback(async () => {
    try {
      setItems((await api.get<QueueItem[]>(base)).data);
      setUnavailable(false);
    } catch (error) {
      if ((error as { response?: { status?: number } }).response?.status === 404)
        setUnavailable(true);
      else setError(failure(error));
    }
  }, [api, base]);
  useEffect(() => {
    void reload();
  }, [reload]);
  async function open(id: string, version?: number) {
    setBusy(true);
    setError('');
    try {
      setRevision(
        (await api.get<Revision>(`${base}/${id}${version ? `?version=${version}` : ''}`)).data
      );
      setConfirmed(false);
      setEvidenceIds([]);
      setExemption('');
      setReason('');
    } catch (error) {
      setError(failure(error));
    } finally {
      setBusy(false);
    }
  }
  async function decide(decision: string) {
    if (!revision) return;
    setBusy(true);
    setError('');
    try {
      const result = await api.post<Revision>(
        `${base}/${revision.campaignId}/${operator ? 'review' : 'accept'}`,
        {
          expectedVersion: revision.version,
          contentHash: revision.contentHash,
          decision,
          reason,
          exemptionExplanation: exemption,
          evidenceIds,
        }
      );
      setRevision(result.data);
      setConfirmed(false);
      await reload();
    } catch (error) {
      setError(failure(error));
    } finally {
      setBusy(false);
    }
  }
  async function download(id: string, name: string) {
    if (!revision) return;
    try {
      const { data } = await api.get<Blob>(`${base}/${revision.campaignId}/evidence/${id}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      setError(failure(error));
    }
  }
  async function upload(file: File) {
    if (!revision) return;
    setBusy(true);
    setError('');
    try {
      if (file.size > 1000000) throw new Error('Nachweise dürfen höchstens 1 MB groß sein.');
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = '';
      for (const byte of bytes) binary += String.fromCharCode(byte);
      await api.post(`/clinical-trials/marketing/approval/${revision.campaignId}/evidence`, {
        expectedVersion: revision.version,
        contentHash: revision.contentHash,
        name: file.name,
        content: btoa(binary),
        encoding: 'base64',
        mediaType: file.type === 'application/pdf' ? 'application/pdf' : 'text/plain',
      });
      await open(revision.campaignId);
    } catch (error) {
      setError(error instanceof Error && !('response' in error) ? error.message : failure(error));
    } finally {
      setBusy(false);
    }
  }
  if (unavailable) return null;
  const snapshot = revision?.snapshot;
  return (
    <section
      aria-label={operator ? 'Cortex Materialprüfung' : 'Kampagne akzeptieren'}
      className="space-y-4 rounded-3xl border p-4 m-4"
    >
      <h2 className="text-lg font-semibold">
        {operator ? 'Cortex Materialprüfung' : 'Kampagne akzeptieren'}
      </h2>
      {error && <p role="alert">{error}</p>}
      <Button
        type="button"
        variant="outline"
        disabled={busy}
        onClick={() => {
          setError('');
          void reload();
          if (revision) void open(revision.campaignId);
        }}
      >
        Prüfstand neu laden
      </Button>
      {!items.length && !error && <p>Keine Kampagnen zur Materialprüfung.</p>}
      <nav aria-label="Vorbereitete Kampagnen" className="flex flex-wrap gap-2">
        {items.map(item => (
          <Button
            type="button"
            variant="outline"
            key={item.id}
            disabled={busy}
            onClick={() => void open(item.id)}
          >
            {item.name}, Revision {item.version}: {states[item.state] ?? item.state}
          </Button>
        ))}
      </nav>
      {revision && snapshot && (
        <div className="space-y-4 min-w-0 break-words">
          <h3 className="font-semibold">
            {snapshot.name}, Revision {revision.version}
          </h3>
          <p role="status">{states[revision.state] ?? revision.state}</p>
          <p className="break-all text-xs">Revisionskennung {revision.contentHash}</p>
          <dl className="grid gap-3 md:grid-cols-2">
            <div>
              <dt>Studien und Prüfstelle</dt>
              <dd>
                {snapshot.study_ids.join(', ')} / {snapshot.site_id}
              </dd>
            </div>
            <div>
              <dt>Erkrankung</dt>
              <dd>{snapshot.disease_focus}</dd>
            </div>
            <div>
              <dt>Auftraggebende Organisation</dt>
              <dd>{snapshot.commissioningOrganizationId}</dd>
            </div>
            <div>
              <dt>Kostenträger</dt>
              <dd>{snapshot.payerOrganizationId ?? 'Noch nicht zugewiesen'}</dd>
            </div>
            <div>
              <dt>Öffentliche Identität</dt>
              <dd>{snapshot.publicIdentity}</dd>
            </div>
            <div>
              <dt>Zeitraum</dt>
              <dd>
                {snapshot.startsOn} bis {snapshot.endsOn}
              </dd>
            </div>
            <div>
              <dt>Zielgruppe</dt>
              <dd>
                Deutschland, Deutsch, Erwachsene ab {snapshot.targeting.minimumAge}, eigene
                Studiensuche. {snapshot.targeting.locations.join(', ')}
              </dd>
            </div>
            {snapshot.placements.some(p => p.channel === 'google_search') && (
              <div>
                <dt>Suchbegriffe</dt>
                <dd>{snapshot.targeting.keywords.join(', ')}</dd>
              </div>
            )}
            <div>
              <dt>Kanalbudgets für Werbung</dt>
              <dd>
                {Object.entries(snapshot.allocationsCents).map(([channel, cents]) => (
                  <p key={channel}>
                    {channel}: {money(cents)}
                  </p>
                ))}
              </dd>
            </div>
            <div>
              <dt>Gesamtbudget vor Steuern</dt>
              <dd>
                {money(snapshot.totalBudgetCents)}, davon Verwaltungsgebühr{' '}
                {money(snapshot.managementFeeCents)} bei 30 % der Werbeausgaben
              </dd>
            </div>
            <div>
              <dt>Testplan</dt>
              <dd>
                Kreativvergleich, fester Gesprächseinstieg. {snapshot.testPlan.durationDays} Tage,{' '}
                {money(snapshot.testPlan.limitCents)} Werbebudget. Pause zur Cortex-Entscheidung am
                Limit.
              </dd>
            </div>
          </dl>
          {snapshot.placements.map((placement, index) => (
            <article
              key={index}
              className="space-y-3 rounded-3xl border p-4"
              aria-label={`Vorschau ${placement.placement}`}
            >
              <h4 className="font-semibold">
                {placement.channel} / {placement.placement}
              </h4>
              <p className="whitespace-pre-wrap">{placement.wording}</p>
              {placement.googleSearch && <GoogleSearchPreview settings={placement.googleSearch} />}
              {placement.assets.map(asset => (
                <VersionImage
                  key={asset.assetId}
                  api={api}
                  base={`${base}/${revision.campaignId}`}
                  asset={asset}
                />
              ))}
              <dl>
                <dt>Zieladresse</dt>
                <dd className="break-all">{placement.entryUrl}</dd>
                <dt>Quelle</dt>
                <dd>{placement.sourceId}</dd>
                <dt>Gesprächseinstieg</dt>
                <dd>{placement.conversationVariant}</dd>
              </dl>
            </article>
          ))}
          <fieldset className="space-y-2" disabled={busy}>
            <legend>Freigabenachweise dieser Revision</legend>
            {!revision.evidence.length && (
              <p>Keine Nachweise angehängt. Das gilt nicht als Ausnahme.</p>
            )}
            {revision.evidence.map(evidence => (
              <div key={evidence.id} className="space-y-1 rounded-3xl border p-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void download(evidence.id, evidence.name)}
                >
                  {evidence.name} herunterladen
                </Button>
                <p className="text-xs break-all">SHA-256 {evidence.sha256}</p>
                {operator && revision.state === 'accepted' && (
                  <label className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={evidenceIds.includes(evidence.id)}
                      onChange={event =>
                        setEvidenceIds(ids =>
                          event.target.checked
                            ? [...ids, evidence.id]
                            : ids.filter(id => id !== evidence.id)
                        )
                      }
                    />
                    {evidence.name} geprüft
                  </label>
                )}
              </div>
            ))}
            {['prepared', 'accepted'].includes(revision.state) && (
              <label className="block">
                Nachweis hochladen, PDF oder Text, höchstens 1 MB
                <input
                  type="file"
                  accept=".pdf,.txt"
                  className="block"
                  onChange={event => {
                    const file = event.target.files?.[0];
                    if (file) void upload(file);
                    event.target.value = '';
                  }}
                />
              </label>
            )}
          </fieldset>
          {!operator && revision.state === 'prepared' && (
            <div className="space-y-3">
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={event => setConfirmed(event.target.checked)}
                />
                Ich habe die Materialien und den Plan dieser Revision geprüft.
              </label>
              <Button
                type="button"
                disabled={busy || !confirmed}
                onClick={() => void decide('accepted')}
              >
                Revision {revision.version} akzeptieren
              </Button>
            </div>
          )}
          {operator && revision.state === 'accepted' && (
            <fieldset className="space-y-3" disabled={busy}>
              <legend>Cortex-Entscheidung</legend>
              <label className="block">
                Geprüfte Begründung, wenn kein Nachweis erforderlich ist
                <Textarea value={exemption} onChange={event => setExemption(event.target.value)} />
              </label>
              <label className="block">
                Begründung für Rückgabe oder Ablehnung
                <Textarea value={reason} onChange={event => setReason(event.target.value)} />
              </label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={!evidenceIds.length && !exemption.trim()}
                  onClick={() => void decide('approved')}
                >
                  Materialien freigeben
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!reason.trim()}
                  onClick={() => void decide('returned')}
                >
                  Zur Änderung zurückgeben
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!reason.trim()}
                  onClick={() => void decide('rejected')}
                >
                  Ablehnen
                </Button>
              </div>
            </fieldset>
          )}
          <p>
            Die Materialfreigabe bestätigt keine Finanzierung oder Anbieterbereitschaft. Höhere
            Budgets benötigen die Zustimmung des Kostenträgers. Bezahlte Auslieferung bleibt
            gesperrt.
          </p>
          <details>
            <summary>Revisions- und Entscheidungshistorie</summary>
            <ol className="space-y-3">
              {revision.history.map((event, index) => (
                <li key={index} className="rounded-3xl border p-3">
                  <p>
                    Revision {event.version}: {states[event.decision] ?? event.decision}
                  </p>
                  <p>
                    {event.actorId}, {event.at}
                  </p>
                  {event.reason && (
                    <dl>
                      <dt>{event.decision === 'rejected' ? 'Ablehnungsgrund' : 'Rückgabegrund'}</dt>
                      <dd className="whitespace-pre-wrap">{event.reason}</dd>
                    </dl>
                  )}
                  {event.exemptionExplanation && (
                    <p className="whitespace-pre-wrap">
                      Geprüfte Ausnahme: {event.exemptionExplanation}
                    </p>
                  )}
                  {event.evidenceIds?.length ? (
                    <p>Geprüfte Nachweise: {event.evidenceIds.join(', ')}</p>
                  ) : null}
                  {event.decision === 'prepared' && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void open(revision.campaignId, event.version)}
                    >
                      Revision {event.version} ansehen
                    </Button>
                  )}
                </li>
              ))}
            </ol>
          </details>
        </div>
      )}
    </section>
  );
}
